import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import type { AiSpreadsheetValueType } from "@/payroll/ai-script/types";
import type { AiScriptCellLink } from "./ai-script-spreadsheet-viewer-types";
import { CELL_FORMAT_OPTIONS } from "./cell-format-options";

// The right-click cell context menu: insert a row above/below, and (when the
// clicked cell is editable) re-format it. `col`/`gridRow` are 0-based grid
// coords of the clicked cell.
export interface RowInsertMenuTarget {
  x: number;
  y: number;
  gridRow: number;
  col: number;
  getNoteGridText?: () => string;
}

export type RowInsertPosition = "above" | "below";

interface RowInsertMenuProps {
  target: RowInsertMenuTarget | null;
  // Whether the row actions (insert above/below) apply — false on a sheet the
  // user can only view, where the menu may still offer comments.
  canInsertRow?: boolean;
  cellLink?: AiScriptCellLink | null;
  onShowSource?: (() => void) | null;
  onAskAi?: () => void;
  onViewEdit?: () => void;
  onNoteForNextPeriod?: () => void;
  onFreezeRows?: () => void;
  onUnfreezeRows?: () => void;
  isLayoutSaving?: boolean;
  isFreezeRowsDisabled?: boolean;
  // Label for the comment action, or null to omit it.
  commentActionLabel?: string | null;
  onCommentAction?: () => void;
  // Whether the clicked cell can be re-formatted (editable + not locked + has a
  // record); the "Format cell" submenu only renders when true.
  canFormatCell: boolean;
  // The clicked cell's current value_type, for the submenu checkmark.
  currentValueType: string | null;
  // Whether the clicked row is a user-added (manual) row that can be deleted;
  // the "Delete row" item only renders when true (generated rows can't).
  canDeleteRow: boolean;
  /**
   * Label for the table-definition action, or null to omit it — defining a table
   * belongs on the selection you are pointing at, not in the toolbar, so it lives
   * here rather than as a permanent top-level control.
   */
  tableActionLabel: string | null;
  /** True while a filter or sort is on: the boundaries would be screen positions. */
  isTableActionDisabled: boolean;
  onClose: () => void;
  onInsertRow: (row: number, position: RowInsertPosition) => void;
  onFormatCell: (valueType: AiSpreadsheetValueType) => void;
  onDeleteRow: () => void;
  onTableAction: () => void;
}

export const RowInsertMenu = ({
  target,
  canInsertRow = true,
  cellLink,
  onShowSource,
  onAskAi,
  onViewEdit,
  onNoteForNextPeriod,
  onFreezeRows,
  onUnfreezeRows,
  isLayoutSaving = false,
  isFreezeRowsDisabled = false,
  commentActionLabel = null,
  onCommentAction,
  canFormatCell,
  currentValueType,
  canDeleteRow,
  tableActionLabel,
  isTableActionDisabled,
  onClose,
  onInsertRow,
  onFormatCell,
  onDeleteRow,
  onTableAction,
}: RowInsertMenuProps) => (
  <DropdownMenu
    open={target !== null}
    onOpenChange={(open, details) => {
      if (!open && details.reason === "focus-out") {
        details.cancel();
        return;
      }
      if (!open) onClose();
    }}
    modal={false}
  >
    <DropdownMenuTrigger
      nativeButton={false}
      render={
        <span
          aria-hidden
          className="pointer-events-none fixed h-0 w-0"
          style={{ left: target?.x ?? 0, top: target?.y ?? 0 }}
        />
      }
    />
    <DropdownMenuContent align="start" className="min-w-max">
      {cellLink && (
        <DropdownMenuItem
          onClick={() => {
            cellLink.onOpen();
            onClose();
          }}
        >
          Open {cellLink.label}
        </DropdownMenuItem>
      )}
      {onShowSource && (
        <DropdownMenuItem
          onClick={() => {
            onShowSource();
            onClose();
          }}
        >
          Show source
        </DropdownMenuItem>
      )}
      {onViewEdit && (
        <DropdownMenuItem onClick={onViewEdit}>View edit</DropdownMenuItem>
      )}
      {onNoteForNextPeriod && (
        <DropdownMenuItem onClick={onNoteForNextPeriod}>
          Note for next open period
        </DropdownMenuItem>
      )}
      {onAskAi && (
        <DropdownMenuItem onClick={onAskAi}>
          Ask AI about this figure
        </DropdownMenuItem>
      )}
      {commentActionLabel !== null && (
        <>
          <DropdownMenuItem onClick={onCommentAction}>
            {commentActionLabel}
          </DropdownMenuItem>
          {(canInsertRow || tableActionLabel !== null) && (
            <DropdownMenuSeparator />
          )}
        </>
      )}
      {tableActionLabel !== null && (
        <>
          <DropdownMenuItem
            disabled={isTableActionDisabled}
            onClick={onTableAction}
          >
            {tableActionLabel}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      {onFreezeRows && (
        <>
          <DropdownMenuItem
            disabled={isLayoutSaving || isFreezeRowsDisabled}
            title={
              isFreezeRowsDisabled
                ? "Clear sorting and filters before freezing these rows."
                : undefined
            }
            onClick={onFreezeRows}
          >
            Freeze through this row
          </DropdownMenuItem>
          {onUnfreezeRows && (
            <DropdownMenuItem
              disabled={isLayoutSaving}
              onClick={onUnfreezeRows}
            >
              Unfreeze rows
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
        </>
      )}
      {canInsertRow && (
        <>
          <DropdownMenuItem
            disabled={!target || target.gridRow < 1}
            onClick={() => {
              if (target) onInsertRow(target.gridRow, "above");
            }}
          >
            Insert row above
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              if (target) onInsertRow(target.gridRow, "below");
            }}
          >
            Insert row below
          </DropdownMenuItem>
        </>
      )}
      {canFormatCell && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Format cell</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {CELL_FORMAT_OPTIONS.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={currentValueType === option.value}
                closeOnClick
                onClick={() => onFormatCell(option.value)}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {canDeleteRow && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={onDeleteRow}>
            Delete row
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);
